import React, { useState, useEffect, useRef } from 'react';
import { getDatabase, ref, get, set, update } from 'firebase/database';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { app } from '../../firebase';
import {
  FaLink, FaUpload, FaDownload, FaExternalLinkAlt,
  FaArrowLeft, FaSearch, FaChartBar,
} from 'react-icons/fa';

// ─── Constants ────────────────────────────────────────────────

const STATUS_OPTIONS = ['Not Started', 'Reached Out', 'Listed', 'Rejected'];

const STATUS_STYLES = {
  'Not Started': 'bg-gray-100 text-gray-600',
  'Reached Out': 'bg-yellow-100 text-yellow-700',
  'Listed':      'bg-green-100 text-green-700',
  'Rejected':    'bg-red-100 text-red-600',
};

const PHASE_STYLES = {
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-purple-100 text-purple-700',
  3: 'bg-orange-100 text-orange-700',
  4: 'bg-teal-100 text-teal-700',
};

const CATEGORIES = [
  'All',
  'Blogging Platforms',
  'Jamaica Directories',
  'Caribbean Directories',
  'Global Directories',
  'Profile Creation',
  'Social Bookmarking',
  'Article Submission',
];

// ─── Default sites ────────────────────────────────────────────

const DEFAULT_SITES = [
  // BLOGGING PLATFORMS
  { id: 'medium-com', name: 'Medium.com', url: 'https://medium.com', category: 'Blogging Platforms', da: 96, type: 'NoFollow', phase: 2, status: 'Not Started', notes: 'NoFollow but valuable for traffic. Post job market insights & career tips for Jamaicans.', dateUpdated: null },
  { id: 'blogger-com', name: 'Blogger.com', url: 'https://blogger.com', category: 'Blogging Platforms', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Google-owned. Create a HireMeJA blog subdomain.', dateUpdated: null },
  { id: 'wordpress-com', name: 'WordPress.com', url: 'https://wordpress.com', category: 'Blogging Platforms', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Free tier = NoFollow. Paid plan = Dofollow.', dateUpdated: null },
  { id: 'linkedin-articles', name: 'LinkedIn Articles', url: 'https://linkedin.com', category: 'Blogging Platforms', da: 98, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Post thought leadership on Caribbean job markets. Perfect for B2B (employers).', dateUpdated: null },
  { id: 'tumblr', name: 'Tumblr', url: 'https://tumblr.com', category: 'Blogging Platforms', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Visual content + blog posts.', dateUpdated: null },
  { id: 'substack', name: 'Substack', url: 'https://substack.com', category: 'Blogging Platforms', da: null, type: 'Mixed', phase: 2, status: 'Not Started', notes: 'Growing newsletter/blog platform.', dateUpdated: null },

  // JAMAICA DIRECTORIES
  { id: 'businessja-com', name: 'BusinessJA.com', url: 'https://www.businessja.com', category: 'Jamaica Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: "Jamaica's largest business directory. Free listing available.", dateUpdated: null },
  { id: 'jamaicaindex-com', name: 'JamaicaIndex.com', url: 'https://www.jamaicaindex.com', category: 'Jamaica Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Verified Jamaica business directory.', dateUpdated: null },
  { id: 'jamaicanbusinessdirectory', name: 'Jamaican Business Directory', url: 'https://www.jamaicanbusinessdirectory.com', category: 'Jamaica Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Free Bronze listing. Paid tiers: Silver/Gold/Diamond.', dateUpdated: null },
  { id: 'findyello-jamaica', name: 'FindYello Jamaica', url: 'https://www.findyello.com/jamaica', category: 'Jamaica Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: "Jamaica's Yellow Pages equivalent.", dateUpdated: null },
  { id: 'zipleaf-jamaica', name: 'ZipLeaf Jamaica', url: 'https://jm.zipleaf.com', category: 'Jamaica Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Large online business directory.', dateUpdated: null },
  { id: 'jamaican-listings', name: 'Jamaican Listings', url: 'https://www.jamaicanbusinessdirectory.com/listing', category: 'Jamaica Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: '', dateUpdated: null },

  // CARIBBEAN DIRECTORIES
  { id: 'bizcaricom', name: 'BizCaricom', url: 'https://bizcaricom.com', category: 'Caribbean Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Best Caribbean-wide business directory. Covers all Caribbean nations.', dateUpdated: null },
  { id: 'caribbeanbusiness-directory', name: 'Caribbean Business Directory', url: 'https://www.caribbeanbusiness.directory', category: 'Caribbean Directories', da: null, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Free listings available.', dateUpdated: null },

  // GLOBAL DIRECTORIES
  { id: 'google-my-business', name: 'Google My Business', url: 'https://business.google.com', category: 'Global Directories', da: 100, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Essential for Local SEO.', dateUpdated: null },
  { id: 'yelp', name: 'Yelp', url: 'https://yelp.com', category: 'Global Directories', da: 95, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'yellow-pages', name: 'Yellow Pages', url: 'https://yellowpages.com', category: 'Global Directories', da: 90, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'bbb', name: 'BBB (Better Business Bureau)', url: 'https://bbb.org', category: 'Global Directories', da: 90, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'owler', name: 'Owler', url: 'https://owler.com', category: 'Global Directories', da: 80, type: 'Dofollow', phase: 3, status: 'Not Started', notes: 'Dofollow confirmed.', dateUpdated: null },
  { id: 'manta', name: 'Manta', url: 'https://manta.com', category: 'Global Directories', da: 85, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'hotfrog', name: 'Hotfrog', url: 'https://hotfrog.com', category: 'Global Directories', da: 75, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'brownbook', name: 'Brownbook', url: 'https://brownbook.net', category: 'Global Directories', da: 65, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'cybo', name: 'Cybo', url: 'https://cybo.com', category: 'Global Directories', da: 70, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'foursquare', name: 'Foursquare', url: 'https://foursquare.com', category: 'Global Directories', da: 90, type: 'Dofollow', phase: 3, status: 'Not Started', notes: '', dateUpdated: null },

  // PROFILE CREATION
  { id: 'crunchbase', name: 'Crunchbase', url: 'https://crunchbase.com', category: 'Profile Creation', da: 92, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Add HireMeJA as a startup.', dateUpdated: null },
  { id: 'angellist', name: 'AngelList', url: 'https://angel.co', category: 'Profile Creation', da: 90, type: 'Dofollow', phase: 1, status: 'Not Started', notes: 'Startup profile.', dateUpdated: null },
  { id: 'producthunt', name: 'ProductHunt', url: 'https://producthunt.com', category: 'Profile Creation', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Launch HireMeJA on the platform.', dateUpdated: null },
  { id: 'github', name: 'GitHub', url: 'https://github.com', category: 'Profile Creation', da: 95, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'If you have open-source components.', dateUpdated: null },
  { id: 'behance', name: 'Behance', url: 'https://behance.net', category: 'Profile Creation', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Design portfolio for your brand.', dateUpdated: null },
  { id: 'dribbble', name: 'Dribbble', url: 'https://dribbble.com', category: 'Profile Creation', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Design showcase.', dateUpdated: null },

  // SOCIAL BOOKMARKING
  { id: 'reddit', name: 'Reddit', url: 'https://reddit.com', category: 'Social Bookmarking', da: 96, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'r/Jamaica, r/CaribbeanJobs (create it!)', dateUpdated: null },
  { id: 'mix-com', name: 'Mix (StumbleUpon)', url: 'https://mix.com', category: 'Social Bookmarking', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: 'Formerly StumbleUpon.', dateUpdated: null },
  { id: 'digg', name: 'Digg', url: 'https://digg.com', category: 'Social Bookmarking', da: 92, type: 'Dofollow', phase: 2, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'flipboard', name: 'Flipboard', url: 'https://flipboard.com', category: 'Social Bookmarking', da: 90, type: 'Dofollow', phase: 2, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'pocket', name: 'Pocket', url: 'https://getpocket.com', category: 'Social Bookmarking', da: 95, type: 'Dofollow', phase: 2, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'pearltrees', name: 'Pearltrees', url: 'https://pearltrees.com', category: 'Social Bookmarking', da: 70, type: 'Dofollow', phase: 2, status: 'Not Started', notes: '', dateUpdated: null },

  // ARTICLE SUBMISSION
  { id: 'ezinearticles', name: 'EzineArticles', url: 'https://ezinearticles.com', category: 'Article Submission', da: 80, type: 'Dofollow', phase: 4, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'articlesfactory', name: 'ArticlesFactory', url: 'https://articlesfactory.com', category: 'Article Submission', da: 60, type: 'Dofollow', phase: 4, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'sooperarticles', name: 'SooperArticles', url: 'https://sooperarticles.com', category: 'Article Submission', da: 55, type: 'Dofollow', phase: 4, status: 'Not Started', notes: '', dateUpdated: null },
  { id: 'articlebiz', name: 'ArticleBiz', url: 'https://articlebiz.com', category: 'Article Submission', da: 50, type: 'Dofollow', phase: 4, status: 'Not Started', notes: '', dateUpdated: null },
];

// ─── Helpers ──────────────────────────────────────────────────

const toSlug = (name) =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');

const fmtDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

// ─── Component ────────────────────────────────────────────────

const BacklinkTracker = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState('All');
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileInputRef = useRef(null);
  const db = getDatabase(app);

  // ── Load from Firebase ──
  useEffect(() => {
    const load = async () => {
      const snap = await get(ref(db, 'marketing/backlinks'));
      if (snap.exists()) {
        const stored = snap.val();
        const defaultIds = new Set(DEFAULT_SITES.map((s) => s.id));
        // Merge defaults with stored (stored values win)
        const merged = DEFAULT_SITES.map((s) =>
          stored[s.id] ? { ...s, ...stored[s.id] } : s
        );
        // Append any custom sites added via Excel
        const custom = Object.values(stored).filter(
          (s) => !defaultIds.has(s.id)
        );
        setSites([...merged, ...custom]);
      } else {
        const batch = {};
        DEFAULT_SITES.forEach((s) => { batch[s.id] = s; });
        await set(ref(db, 'marketing/backlinks'), batch);
        setSites(DEFAULT_SITES);
      }
      setLoading(false);
    };
    load();
  }, []);

  // ── Status change ──
  const updateStatus = async (id, status) => {
    const dateUpdated = new Date().toISOString();
    await update(ref(db, `marketing/backlinks/${id}`), {
      status, dateUpdated,
    });
    setSites((prev) =>
      prev.map((s) => s.id === id ? { ...s, status, dateUpdated } : s)
    );
  };

  // ── Save note ──
  const saveNote = async (id, notes) => {
    await update(ref(db, `marketing/backlinks/${id}`), { notes });
    setSites((prev) =>
      prev.map((s) => s.id === id ? { ...s, notes } : s)
    );
    setEditingNote(null);
  };

  // ── Excel upload ──
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);
        const updates = {};
        const newSites = [...sites];
        let changed = 0;

        rows.forEach((row) => {
          const name = String(row.Name || row.name || '').trim();
          if (!name) return;
          const idx = newSites.findIndex(
            (s) => s.name.toLowerCase() === name.toLowerCase()
          );
          const dateUpdated = new Date().toISOString();

          if (idx !== -1) {
            const updated = {
              ...newSites[idx],
              ...(row.Status ? { status: row.Status } : {}),
              ...(row.Notes !== undefined ? { notes: String(row.Notes) } : {}),
              ...(row.URL ? { url: row.URL } : {}),
              ...(row.DA ? { da: Number(row.DA) } : {}),
              dateUpdated,
            };
            newSites[idx] = updated;
            updates[`marketing/backlinks/${updated.id}`] = updated;
            changed++;
          } else {
            const id = toSlug(name);
            const newSite = {
              id,
              name,
              url: row.URL || '',
              category: row.Category || 'Other',
              da: row.DA ? Number(row.DA) : null,
              type: row.Type || 'Unknown',
              phase: row.Phase ? Number(row.Phase) : 1,
              status: row.Status || 'Not Started',
              notes: row.Notes ? String(row.Notes) : '',
              dateUpdated,
            };
            newSites.push(newSite);
            updates[`marketing/backlinks/${id}`] = newSite;
            changed++;
          }
        });

        if (Object.keys(updates).length > 0) {
          await update(ref(db, '/'), updates);
          setSites(newSites);
          setUploadMsg(`${changed} site${changed !== 1 ? 's' : ''} updated from Excel.`);
          setTimeout(() => setUploadMsg(null), 4000);
        }
      } catch (err) {
        setUploadMsg('Upload failed. Check file format and try again.');
        setTimeout(() => setUploadMsg(null), 4000);
      }
      e.target.value = '';
    };
    reader.readAsBinaryString(file);
  };

  // ── Excel export ──
  const handleExport = () => {
    const rows = sites.map((s) => ({
      Name: s.name,
      URL: s.url,
      Category: s.category,
      DA: s.da ?? '',
      Type: s.type,
      Phase: s.phase,
      Status: s.status,
      Notes: s.notes || '',
      'Date Updated': fmtDate(s.dateUpdated) || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    // Column widths
    ws['!cols'] = [
      { wch: 28 }, { wch: 40 }, { wch: 22 }, { wch: 6 },
      { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 50 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Backlinks');
    XLSX.writeFile(wb, 'HireMeJA_Backlinks.xlsx');
  };

  // ── Stats ──
  const total = sites.length;
  const listed = sites.filter((s) => s.status === 'Listed').length;
  const reachedOut = sites.filter((s) => s.status === 'Reached Out').length;
  const rejected = sites.filter((s) => s.status === 'Rejected').length;
  const notStarted = sites.filter((s) => s.status === 'Not Started').length;
  const pct = total > 0 ? Math.round((listed / total) * 100) : 0;

  const phaseStats = [1, 2, 3, 4].map((p) => ({
    phase: p,
    total: sites.filter((s) => s.phase === p).length,
    listed: sites.filter((s) => s.phase === p && s.status === 'Listed').length,
    reached: sites.filter((s) => s.phase === p && s.status === 'Reached Out').length,
  }));

  // ── Filtered list ──
  const filtered = sites.filter((s) => {
    if (phaseFilter !== 'All' && s.phase !== Number(phaseFilter)) return false;
    if (catFilter !== 'All' && s.category !== catFilter) return false;
    if (statusFilter !== 'All' && s.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !s.url.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <div className="w-8 h-8 border-4 border-blue-950 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/marketing"
            className="p-2 rounded-md bg-white border border-gray-200 text-gray-500 hover:text-blue-950 hover:border-blue-950 transition-colors"
          >
            <FaArrowLeft size={14} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-blue-950">Backlink Tracker</h1>
            <p className="text-sm text-gray-500 mt-0.5">Dofollow outreach checklist — hiremeja.com</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {uploadMsg && (
            <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-md">
              {uploadMsg}
            </span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleUpload}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-gray-200 text-sm text-gray-600 hover:border-blue-950 hover:text-blue-950 transition-colors"
          >
            <FaUpload size={12} /> Upload Excel
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-gray-200 text-sm text-gray-600 hover:border-blue-950 hover:text-blue-950 transition-colors"
          >
            <FaDownload size={12} /> Export Excel
          </button>
        </div>
      </div>

      {/* Upload hint */}
      <div className="text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-md px-4 py-2">
        <strong>Excel import columns:</strong> Name · URL · Category · DA · Type · Phase · Status · Notes
        &nbsp;|&nbsp; Status values: <em>Not Started, Reached Out, Listed, Rejected</em>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-950">{total}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Sites</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{listed}</p>
          <p className="text-xs text-gray-500 mt-0.5">Listed</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-yellow-700">{reachedOut}</p>
          <p className="text-xs text-gray-500 mt-0.5">Reached Out</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{rejected}</p>
          <p className="text-xs text-gray-500 mt-0.5">Rejected</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-950">{pct}%</p>
          <p className="text-xs text-gray-500 mt-0.5">Complete</p>
        </div>
      </div>

      {/* Phase progress */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <FaChartBar size={14} className="text-blue-950" />
          <h3 className="text-sm font-bold text-blue-950">Phase Progress</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {phaseStats.map(({ phase, total: pTotal, listed: pListed, reached: pReached }) => {
            const phasePct = pTotal > 0 ? Math.round((pListed / pTotal) * 100) : 0;
            const phaseNames = { 1: 'Foundation', 2: 'Content Distribution', 3: 'Global Directories', 4: 'Niche Content' };
            return (
              <div key={phase}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PHASE_STYLES[phase]}`}>
                    Phase {phase}
                  </span>
                  <span className="text-xs text-gray-500">{pListed}/{pTotal} listed</span>
                </div>
                <p className="text-xs text-gray-400 mb-1.5">{phaseNames[phase]}</p>
                <div className="h-2 bg-gray-100 rounded-full">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      phase === 1 ? 'bg-blue-500' : phase === 2 ? 'bg-purple-500' :
                      phase === 3 ? 'bg-orange-500' : 'bg-teal-500'
                    }`}
                    style={{ width: `${phasePct}%` }}
                  />
                </div>
                {pReached > 0 && (
                  <p className="text-xs text-yellow-600 mt-1">{pReached} in progress</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Phase tabs */}
          <div className="flex gap-1">
            {['All', '1', '2', '3', '4'].map((p) => (
              <button
                key={p}
                onClick={() => setPhaseFilter(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  phaseFilter === p
                    ? 'bg-blue-950 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p === 'All' ? 'All Phases' : `Phase ${p}`}
              </button>
            ))}
          </div>

          <div className="flex gap-2 flex-wrap flex-1">
            {/* Category select */}
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 text-gray-600 focus:outline-none focus:border-blue-950"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>
              ))}
            </select>

            {/* Status select */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 text-gray-600 focus:outline-none focus:border-blue-950"
            >
              <option value="All">All Statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {/* Search */}
            <div className="relative">
              <FaSearch size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search sites..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="text-xs border border-gray-200 rounded-md pl-7 pr-3 py-1.5 text-gray-600 focus:outline-none focus:border-blue-950 w-40"
              />
            </div>

            <span className="ml-auto text-xs text-gray-400 self-center">
              {filtered.length} site{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-blue-950 text-white">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold w-36">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold">Site</th>
                <th className="text-left px-4 py-3 text-xs font-semibold w-36">Category</th>
                <th className="text-center px-3 py-3 text-xs font-semibold w-16">Phase</th>
                <th className="text-left px-4 py-3 text-xs font-semibold">Notes</th>
                <th className="text-right px-4 py-3 text-xs font-semibold w-28">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-gray-400 text-sm">
                    No sites match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((site, i) => (
                  <tr
                    key={site.id}
                    className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                      i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    {/* Status */}
                    <td className="px-4 py-3">
                      <select
                        value={site.status}
                        onChange={(e) => updateStatus(site.id, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-950/20 ${STATUS_STYLES[site.status]}`}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </td>

                    {/* Site name + badges */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-blue-950">{site.name}</span>
                        {site.da && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                            DA {site.da}
                          </span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          site.type === 'Dofollow'
                            ? 'bg-green-100 text-green-700'
                            : site.type === 'NoFollow'
                            ? 'bg-gray-100 text-gray-500'
                            : 'bg-yellow-100 text-yellow-600'
                        }`}>
                          {site.type}
                        </span>
                        {site.url && (
                          <a
                            href={site.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-blue-950 transition-colors"
                          >
                            <FaExternalLinkAlt size={10} />
                          </a>
                        )}
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{site.category}</span>
                    </td>

                    {/* Phase */}
                    <td className="px-3 py-3 text-center">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PHASE_STYLES[site.phase] || 'bg-gray-100 text-gray-600'}`}>
                        P{site.phase}
                      </span>
                    </td>

                    {/* Notes */}
                    <td className="px-4 py-3 max-w-xs">
                      {editingNote?.id === site.id ? (
                        <input
                          autoFocus
                          className="w-full text-xs border border-blue-950 rounded px-2 py-1 focus:outline-none"
                          value={editingNote.value}
                          onChange={(e) =>
                            setEditingNote({ id: site.id, value: e.target.value })
                          }
                          onBlur={() => saveNote(site.id, editingNote.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNote(site.id, editingNote.value);
                            if (e.key === 'Escape') setEditingNote(null);
                          }}
                        />
                      ) : (
                        <span
                          className={`text-xs cursor-pointer hover:text-blue-950 transition-colors ${
                            site.notes ? 'text-gray-600' : 'text-gray-300 italic'
                          }`}
                          onClick={() =>
                            setEditingNote({ id: site.id, value: site.notes || '' })
                          }
                        >
                          {site.notes || '+ Add note'}
                        </span>
                      )}
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-gray-400">
                        {fmtDate(site.dateUpdated) || '—'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BacklinkTracker;
